import React, { useState, useEffect, useRef } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import 'keen-slider/keen-slider.min.css';

const valuesData = [
    {
        id: 1,
        title: "Talent",
        desc: "We are a team of passionate tech enthusiasts continually exploring new ways to bring the best-in-class IT solutions to our clients."
    },
    {
        id: 2,
        title: "Insights",
        desc: "We are a team of passionate tech enthusiasts continually exploring new ways to bring the best-in-class IT solutions to our clients."
    },
    {
        id: 3,
        title: "Passion",
        desc: "We are a team of passionate tech enthusiasts continually exploring new ways to bring the best-in-class IT solutions to our clients."
    },
    {
        id: 4,
        title: "Integrity",
        desc: "We are a team of passionate tech enthusiasts continually exploring new ways to bring the best-in-class IT solutions to our clients."
    }
];

const OurValuesSection = () => {
    const [activeId, setActiveId] = useState(1);
    const [loaded, setLoaded] = useState(false);
    const autoPlayRef = useRef(null);

    const [sliderRef, instanceRef] = useKeenSlider({
        loop: true,
        initial: 0,
        slides: {
            perView: 1.1,
            spacing: 16,
        },
        breakpoints: {
            '(min-width: 640px)': {
                slides: { perView: 1.8, spacing: 20 },
            },
            '(min-width: 1024px)': {
                slides: { perView: 2.3, spacing: 24 },
            },
            '(min-width: 1440px)': {
                slides: { perView: 2.8, spacing: 24 },
            },
        },
        slideChanged(slider) {
            const relIdx = slider.track.details.rel;
            if (valuesData[relIdx]) {
                setActiveId(valuesData[relIdx].id);
            }
        },
        created(s) {
            setLoaded(true);
            startAutoplay(s);
            s.container.addEventListener("mouseover", () => stopAutoplay());
            s.container.addEventListener("mouseout", () => startAutoplay(s));
        },
        destroyed() {
            stopAutoplay();
        }
    });

    const startAutoplay = (sliderInstance) => {
        stopAutoplay();
        autoPlayRef.current = setInterval(() => {
            if (sliderInstance && !sliderInstance.destroyed) {
                sliderInstance.next();
            }
        }, 4000);
    };

    const stopAutoplay = () => {
        if (autoPlayRef.current) {
            clearInterval(autoPlayRef.current);
        }
    };

    return (
        <section className="w-full bg-[#FFFFFF] pt-0 sm:pb-[100px] lg:pt-0 pb-12 overflow-hidden select-none text-left relative z-10">
            <div className="max-w-[1440px] w-full mx-auto px-5 sm:px-10 lg:px-[80px] lg:pr-0">
                <div className="flex lg:flex-row flex-col">
                    <div className="w-full max-w-[500px]">
                        <h2 className="text-[#01386A] font-sora font-[600] text-[20px] lg:text-[30px] leading-[1.2] tracking-tight">
                            Our Values
                        </h2>


                    </div>

                    <div className="w-full max-w-[851px] flex flex-col items-end relative">

                        <div className="w-full h-[240px] sm:h-[353px] relative overflow-hidden rounded-none">
                            <div
                                className="w-full h-full bg-cover bg-center bg-no-repeat absolute inset-0 filter brightness-90"
                                style={{ backgroundImage: "url('/assets/transforming/img2.png')" }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A2540]/10" />
                        </div>

                        <div className="w-full mt-[-100px] sm:mt-[-240px] relative z-30 flex items-end min-h-[320px]">
                            <div ref={sliderRef} className="keen-slider w-full flex items-end">
                                {valuesData.map((slide) => {
                                    const isSelected = slide.id === activeId;

                                    return (
                                        <div
                                            key={slide.id}
                                            className="keen-slider__slide h-full flex flex-col justify-end"
                                            onMouseEnter={() => setActiveId(slide.id)}
                                        >
                                            <div
                                                className={`w-full h-[260px] sm:h-[300px] p-6 sm:p-8 flex flex-col justify-between items-start rounded-none border transition-all duration-500 relative overflow-hidden group ${isSelected
                                                    ? "bg-[#0A3D62] border-transparent shadow-[0_20px_45px_rgba(10,61,98,0.18)]"
                                                    : "bg-white border-[#E2E8F0] shadow-xs"
                                                    }`}
                                            >
                                                <div className="w-full">
                                                    <h3 className={`font-sora text-[16px] sm:text-[20px] font-[600] tracking-tight transition-colors duration-300 ${isSelected ? "text-white" : "text-[#0A2540]"
                                                        }`}>
                                                        {slide.title}
                                                    </h3>
                                                </div>

                                                <div className="w-full">
                                                    <p className={`font-graphik text-[14px] sm:text-[16px] leading-relaxed font-[500] transition-colors duration-300 ${isSelected ? "text-white/80" : "text-[#4A5568]"
                                                        }`}>
                                                        {slide.desc}
                                                    </p>
                                                </div>

                                                <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#01386A]" />

                                                <div
                                                    className={`absolute bottom-0 left-0 h-[4px] bg-[#ED7A3A] transition-all duration-500 ease-out origin-left z-20 ${isSelected ? "w-full" : "w-0"
                                                        }`}
                                                />

                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {loaded && instanceRef.current && (
                            <div className="flex items-start justify-start w-full lg:pt-[40px] pt-[20px] gap-3 shrink-0">
                                <button
                                    onClick={() => instanceRef.current?.prev()}
                                    className="w-10 h-10 border border-[#ED7A3A] bg-transparent flex items-center justify-center text-[#ED7A3A] hover:bg-[#ED7A3A] hover:text-white transition-all duration-300 cursor-pointer rounded-full"
                                    aria-label="Previous value"
                                >
                                    <ArrowLeft size={18} strokeWidth={2} />
                                </button>
                                <button
                                    onClick={() => instanceRef.current?.next()}
                                    className="w-10 h-10 border border-[#ED7A3A] bg-transparent flex items-center justify-center text-[#ED7A3A] hover:bg-[#ED7A3A] hover:text-white transition-all duration-300 cursor-pointer rounded-full"
                                    aria-label="Next value"
                                >
                                    <ArrowRight size={18} strokeWidth={2} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default OurValuesSection;